import { Card } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { CTSearchView, AccessGridTable, StethoscopeLoading } from '../../components';
import { useDispatch, useSelector } from "react-redux";
import {
    getUserDetailsGet,
    getUsersManagementReportsGet,
    getMRGroupsMstrlst,
    updateMRSItesReports,
    archiveMRSItesReports,
    deleteReports,
} from 'apiServices';
import { useState, useEffect, useRef } from 'react';
import { Edit, Trash2, Archive, RotateCw } from 'feather-icons-react';
import { themeColors } from '../../styles/theme';
import './usersReportsList.css';
import { showPmrSuccess, showPmrDeleteConfirm } from '../../utils/swalUtils';


const UsersReportsList = () => {
    const [loading, setLoading] = useState(false);
    const [usersData, setUsersData] = useState([]);
    const [reportTypes, setReportTypes] = useState([]);
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.apiService.login);
    const token = userData?.Token || '';
    const params = { token, user_id: userData.user_id };
    const [searchQuery, setSearchQuery] = useState('');
    const gridRef = useRef();
    const [searchTerm, setSearchTerm] = useState('');
    const [includeArchive, setIncludeArchive] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editReportData, setEditReportData] = useState(null);
    const [isAddingReportType, setIsAddingReportType] = useState(false);
    const [newReportType, setNewReportType] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);


    const onSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const onClear = () => {
        setSearchTerm("");
    };

    const onEdit = (row) => {
        console.log("Edit clicked:", row);

        dispatch(getMRGroupsMstrlst(params))
            .then((response) => {
                console.log("Report types:", response);
                if (response?.payload?.Table) {
                    // Filter out report types with id:0
                    const filteredReportTypes = response.payload.Table.filter(type => type.id !== 0);
                    setReportTypes(filteredReportTypes);
                    setEditReportData(row);
                    setShowEditModal(true);
                }
            })
            .catch((error) => {
                console.error("Error fetching report types:", error);
            });
    };

    const onDelete = (row) => {
        showPmrDeleteConfirm({
            html: `<div class="pmr-popup-message">You want to delete the report <b>${row.report_name}</b>? It will be permanently deleted.</div>`,
            confirmText: 'Yes',
            cancelText: 'No',
        }).then((result) => {
            if (result.isConfirmed) {
                const formBody = {
                    token: token,
                    LoginUserID: userData.user_id,
                    UserID: row.user_id,
                    ReportID: row.report_id
                };
                dispatch(deleteReports(formBody))
                    .then(() => {
                        setShowDeleteSuccess(true);
                        return dispatch(getUsersManagementReportsGet(params));
                    })
                    .then((response) => {
                        if (response?.payload?.Table) {
                            setUsersData(response.payload.Table);
                        }
                    })
                    .catch((error) => {
                        console.error("Error deleting report:", error);
                    });
            }
        });
    };

    const onArchive = (row) => {
        showPmrDeleteConfirm({
            html: `<div class="pmr-popup-message">You want to archive the report <b>${row.report_name}</b>? It will remain archived for 30 days only, after which it will be permanently deleted.</div>`,
            confirmText: 'Yes',
            cancelText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) return;

            const archiveParams = {
                token: token,
                LoginUserID: userData.user_id,
                ReportID: row.report_id,
                UserID: row.user_id
            };

            dispatch(archiveMRSItesReports(archiveParams))
                .then(() => dispatch(getUsersManagementReportsGet(params)))
                .then((response) => {
                    if (response?.payload?.Table) {
                        setUsersData(response.payload.Table);
                    }
                })
                .catch((error) => {
                    console.error("Error archiving report:", error);
                });
        });
    };

    const actionsCellRenderer = (params) => (
        <>
            <span onClick={() => onEdit(params.data)} style={{ cursor: 'pointer', marginRight: 8 }} title="Edit Report">
                <Edit color={themeColors.primary} size={18} />
            </span>
            <span onClick={() => onArchive(params.data)} style={{ cursor: 'pointer', marginRight: 8 }} title="Archive Report">
                <Archive color="#fca326" size={18} />
            </span>
            <span onClick={() => onDelete(params.data)} style={{ cursor: 'pointer' }} title="Delete Report">
                <Trash2 color="#d32f2f" size={18} />
            </span>
        </>
    );

    const nameCellRenderer = (params) => (
        <div className="d-flex align-items-center">
            <span className="fw-bold text" style={{ cursor: 'pointer', color: 'var(--pmr-secondary)' }}>{params.value}</span>
        </div>
    );

    const userColumns = [
        {
            headerName: "REPORT NAME",
            field: "report_name",
            flex: 2,
            minWidth: 200,
            tooltipField: "report_name",
            headerTooltip: "REPORT NAME",
            cellRenderer: nameCellRenderer,
            cellClass: 'ag-cell-wrap-text',
        },
        {
            headerName: "CATEGORY",
            field: "group_name",
            flex: 1,
            minWidth: 100,
            tooltipField: "group_name",
            headerTooltip: "CATEGORY",
            cellClass: 'ag-cell-wrap-text',
        },
        {
            headerName: "NAME",
            field: "contact_name",
            flex: 1,
            minWidth: 100,
            tooltipField: "contact_name",
            headerTooltip: "NAME",
            cellClass: 'ag-cell-wrap-text',
        },
        {
            headerName: "REPORT DATE",
            field: "report_date",
            width: 150,
            tooltipField: "report_date",
            headerTooltip: "REPORT DATE",
            cellClass: 'ag-cell-wrap-text',
        },
        {
            headerName: "PROVIDER",
            field: "provider_name",
            flex: 1,
            minWidth: 100,
            tooltipField: "provider_name",
            headerTooltip: "PROVIDER",
            cellClass: 'ag-cell-wrap-text',
        },
        {
            headerName: "CLINIC/DIAGNOSTICS CENTER NAME",
            field: "hospital_name",
            flex: 1,
            minWidth: 100,
            tooltipField: "hospital_name",
            headerTooltip: "CLINIC/DIAGNOSTICS CENTER NAME",
            cellClass: 'ag-cell-wrap-text',
        },
        {
            headerName: "ACTIONS",
            field: "actions",
            width: 150,
            tooltipField: "actions",
            headerTooltip: "ACTIONS",
            cellRenderer: actionsCellRenderer,
            headerClass: 'custom-header',
            cellClass: 'text-center'
        }
    ];

    const handleReportClick = (params) => {
        console.log("Report clicked", params.data);
    };

    useEffect(() => {
        dispatch(getUsersManagementReportsGet(params))
            .then((response) => {
                if (response?.payload?.Table) {
                    setUsersData(response.payload.Table);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching reports:", error);
                setLoading(false);
            });
    }, []);



    const handleIncludeArchiveChange = (e) => {
        setIncludeArchive(e.target.checked);
        intialCalls(); // Call your function here
    };

    const intialCalls = () => {
        console.log("Initial data fetch or update triggered");
        // Implement your actual logic
    };

    const onRefreshClick = () => {
        console.log("Refresh clicked");
        // Implement refresh logic here
    };

    const handleSaveChanges = () => {
        setIsSaving(true);
        setSaveError(null);

        const formBody = {
            token: token,
            LoginUserID: userData.user_id,
            UserID: editReportData.user_id,
            GroupID: reportTypes.find(type => type.name === editReportData.group_name)?.id || 0,
            HospitalName: editReportData.hospital_name || null,
            ProviderName: editReportData.provider_name || null,
            ReportDate: editReportData.report_date,
            ReportID: editReportData.report_id,
            ReportName: editReportData.report_name,
            TagNo: editReportData.tag_no || null
        };
        console.log("formBody:", formBody);

        dispatch(updateMRSItesReports(formBody))
            .unwrap()
            .then((response) => {
                console.log("updateMRSItesReports response:", response);
                // Check if response exists and has data
                if (response) {
                    // Success case
                    setShowEditModal(false);
                    setShowSuccessModal(true);
                    // Refresh the grid data
                    return dispatch(getUsersManagementReportsGet(params));
                } else {
                    // Handle unsuccessful response
                    setSaveError("Failed to update report. Please try again.");
                    throw new Error("Update was not successful");
                }
            })
            .then((response) => {
                if (response?.payload?.Table) {
                    setUsersData(response.payload.Table);
                }
            })
            .catch((error) => {
                console.error("Error updating report:", error);
                setSaveError("An error occurred while updating the report. Please try again.");
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    // Add handler for input changes
    const handleInputChange = (field, value) => {
        console.log("handleInputChange", field, value);
        if (field === 'report_name') {
            // For report name, ensure we're only using the input value
            setEditReportData(prev => ({
                ...prev,
                report_name: value
            }));
        } else {
            // For other fields, handle normally
            setEditReportData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    useEffect(() => {
        if (showSuccessModal) {
            showPmrSuccess('Report has been updated successfully.').then(() => setShowSuccessModal(false));
        }
    }, [showSuccessModal]);

    useEffect(() => {
        if (showDeleteSuccess) {
            showPmrSuccess('Report deleted successfully.').then(() => setShowDeleteSuccess(false));
        }
    }, [showDeleteSuccess]);

    return (
        <div className="bg-white rounded shadow-lg py-4 px-3">
            <div className="row">

                {loading ? (
                    <StethoscopeLoading message="Loading..." />
                ) : usersData.length > 0 ? (
                    <>
                        <div className="col-12 px-2 mb-3">
                            <CTSearchView
                                placeholder="Search users..."
                                onChange={onSearch}
                                onChangeclear={onClear}
                            />
                        </div>

                        <div className="d-flex align-items-center justify-content-end w-100 mb-2">
                            <span className="d-flex align-items-center" style={{ marginRight: '0.8rem' }}>
                                <div className="custom-control custom-checkbox">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input "
                                        id="includeArchive"
                                        checked={includeArchive}
                                        onChange={handleIncludeArchiveChange}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    <label className="custom-control-label mr-2" htmlFor="includeArchive" style={{ color: 'red', fontWeight: 500 }}>
                                        Include Archived Reports
                                    </label>
                                </div>
                            </span>
                            <span
                                onClick={onRefreshClick}
                                title="Refresh"
                                style={{ cursor: 'pointer' }}
                                className="d-flex align-items-center text-info"
                            >
                                <RotateCw size={18} style={{ marginRight: '0.5rem' }} />
                                Refresh
                            </span>
                        </div>

                        <div className="ag-theme-balham custom-ag-grid " style={{ height: 'calc(100vh - 200px)', width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={usersData}
                                columnDefs={userColumns}
                                    enableBrowserTooltips={true}
                                    tooltipShowDelay={0}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                    minWidth: 80,
                                    wrapText: true,
                                    autoHeight: true
                                }}
                                rowSelection="multiple"
                                pagination={true}
                                paginationPageSize={11}
                                suppressRowClickSelection={true}
                                headerHeight={44}
                                rowHeight={53}
                                onRowClicked={handleReportClick}
                                rowClass={(params) => {
                                    return params.rowIndex % 2 === 0 ? 'even-row' : 'odd-row';
                                }}
                                getRowStyle={(params) => {
                                    return {
                                        backgroundColor: params.rowIndex % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                        border: 'none',
                                        borderBottom: '1px solid #dee2e6'
                                    };
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <Card className="border-0 h-100">
                        <Card.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh", height: "60vh" }}>
                            <p className="text-muted m-0">No users found.</p>
                        </Card.Body>
                    </Card>
                )}
            </div>

            {showEditModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal" style={{ width: '900px' }}>
                        <div className="custom-modal-header">
                            <span>
                                Edit Report Details <span className="modal-user-name">{editReportData?.contact_name}</span>
                            </span>
                            <span className="close-btn" onClick={() => setShowEditModal(false)}>&times;</span>
                        </div>
                        <div className="custom-modal-body">
                            <div className="form-group">
                                <label>Report Name:</label>
                                <input
                                    className="form-control"
                                    value={editReportData?.report_name}
                                    onChange={(e) => handleInputChange('report_name', e.target.value)}
                                />
                            </div>
                            <div className="form-group report-type-group" style={{ position: 'relative' }}>
                                <label>Report Type:</label>
                                {!isAddingReportType ? (
                                    <>
                                        <select
                                            className="form-control"
                                            value={editReportData?.group_name || ''}
                                            onChange={(e) => handleInputChange('group_name', e.target.value)}
                                        >
                                            {reportTypes.map((type) => (
                                                <option key={type.id} value={type.name}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                        <a
                                            href="#"
                                            className="add-new-link end-link fw-bold"
                                            style={{ color: 'var(--pmr-danger)' }}
                                            onClick={e => {
                                                e.preventDefault();
                                                setIsAddingReportType(true);
                                            }}
                                        >
                                            Report Type not found? Please click here to add New
                                        </a>
                                    </>
                                ) : (
                                    <div >
                                        <input
                                            className="form-control"
                                            placeholder="Enter new report type"
                                            value={newReportType}
                                            onChange={e => setNewReportType(e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0', justifyContent: "flex-end" }}>
                                            <button
                                                className="btn btn-link text fw-bold"
                                                style={{ color: 'var(--pmr-success)', fontSize: '0.9rem', padding: '0 8px', textTransform: 'none' }}
                                                onClick={() => {
                                                    const newType = { id: Date.now(), name: newReportType };
                                                    setReportTypes([...reportTypes, newType]);
                                                    handleInputChange('group_name', newReportType);
                                                    setIsAddingReportType(false);
                                                    setNewReportType('');
                                                }}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="btn btn-link text fw-bold"
                                                style={{ color: 'var(--pmr-danger)', fontSize: '0.9rem', padding: '0 8px', textTransform: 'none' }}
                                                onClick={() => {
                                                    setIsAddingReportType(false);
                                                    setNewReportType('');
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Provider Name:</label>
                                <input
                                    className="form-control"
                                    value={editReportData?.provider_name || ''}
                                    onChange={(e) => handleInputChange('provider_name', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Clinic / Diagnostics Center Name:</label>
                                <input
                                    className="form-control"
                                    value={editReportData?.hospital_name || ''}
                                    onChange={(e) => handleInputChange('hospital_name', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Report Date:</label>
                                <input
                                    className="form-control"
                                    type="date"
                                    value={editReportData?.report_date || ''}
                                    onChange={(e) => handleInputChange('report_date', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="custom-modal-footer center-footer">
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveChanges}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            {saveError && (
                                <div className="text-danger mt-2">{saveError}</div>
                            )}
                            <button
                                className="btn btn-danger"
                                onClick={() => setShowEditModal(false)}
                                disabled={isSaving}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>
    )
}
export default UsersReportsList;